import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { GqlTokenProvider, GqlUnauthorizedProvider } from '../gql/hooks';
import { login, type LoginCredentials, type Operator } from './api';

// Operator auth context.
//
// Holds the Bearer JWT + operator profile, exposes sign-in/sign-out, persists the session,
// and feeds the token into the GraphQL layer (GqlTokenProvider) so every request is
// authenticated. The subscription socket (C29) will read the same token via useAuth().token,
// so the connect path is a one-liner later — no restructuring needed.
//
// ── Token storage tradeoff (per .claude/rules/web/security.md) ───────────────────────────
// The credential is a Bearer JWT carried in the Authorization header (NOT a cookie), so there
// is no ambient-credential CSRF surface to defend: an attacker cannot make the browser attach
// this token to a forged cross-site request. The remaining risk is XSS exfiltration.
//
// 2026-06-06: switched from `sessionStorage` to `localStorage` so the operator stays signed
// in across tabs. Opening the SPA in a second tab no longer forces a re-login. We accept the
// longer persistence window in exchange for the operator UX; the JWT's TTL (8h, set on
// Guardian) caps the actual exposure window. A `storage` event listener picks up sign-in /
// sign-out from other tabs in real time so a sign-out anywhere logs out everywhere — the
// reverse direction of the same affordance. We deliberately do NOT use a JS-readable cookie
// (would add CSRF risk with no XSS benefit).

const STORAGE_KEY = 'aip.admin.session';
// Old sessionStorage key — read on init for a one-time migration so operators who logged in
// before this change don't get bounced back to /login on the next page load.
const LEGACY_SESSION_STORAGE_KEY = 'aip.admin.session';

export type AuthStatus = 'authenticated' | 'unauthenticated';

interface PersistedSession {
  token: string;
  operator: Operator;
}

export interface AuthContextValue {
  token: string | null;
  operator: Operator | null;
  status: AuthStatus;
  isAuthenticated: boolean;
  /** Authenticate via POST /api/session, persist the session, and update context. */
  signIn: (credentials: LoginCredentials) => Promise<void>;
  /** Clear the session (token + operator) and persisted storage. */
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function parseSession(raw: string | null): PersistedSession | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<PersistedSession>;
    if (
      typeof parsed?.token === 'string' &&
      parsed.operator &&
      typeof parsed.operator.id === 'string'
    ) {
      return { token: parsed.token, operator: parsed.operator };
    }
    return null;
  } catch {
    return null;
  }
}

function readPersistedSession(): PersistedSession | null {
  if (typeof localStorage === 'undefined') return null;
  const fromLocal = parseSession(localStorage.getItem(STORAGE_KEY));
  if (fromLocal) return fromLocal;

  // One-time migration: an operator already signed in under the pre-2026-06-06 sessionStorage
  // scheme would otherwise be forced through /login on the first load after deploy. Copy the
  // legacy entry across, then drop it so subsequent tabs see the localStorage value directly.
  if (typeof sessionStorage !== 'undefined') {
    const fromSession = parseSession(sessionStorage.getItem(LEGACY_SESSION_STORAGE_KEY));
    if (fromSession) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(fromSession));
        sessionStorage.removeItem(LEGACY_SESSION_STORAGE_KEY);
      } catch {
        /* If the write fails the legacy in-memory session still drives this tab. */
      }
      return fromSession;
    }
  }
  return null;
}

function persistSession(session: PersistedSession | null): void {
  if (typeof localStorage === 'undefined') return;
  try {
    if (session) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Storage may be unavailable (private mode / quota); auth still works in-memory.
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<PersistedSession | null>(readPersistedSession);

  // Keep storage in sync if the session changes for any reason.
  useEffect(() => {
    persistSession(session);
  }, [session]);

  // Cross-tab sync: a `storage` event fires in EVERY OTHER tab when a tab writes to
  // localStorage. So when one tab signs in (or out), the others pick it up live without a
  // refresh. The event doesn't fire in the tab that did the write, so we never echo our own
  // change. If the value gets parsed as null (sign-out, key removed), state goes back to
  // unauthenticated and `RequireAuth` redirects to /login on the next render.
  useEffect(() => {
    function onStorage(event: StorageEvent) {
      if (event.key !== STORAGE_KEY) return;
      const next = parseSession(event.newValue);
      setSession(next);
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const signIn = useCallback(async (credentials: LoginCredentials) => {
    const { token, operator } = await login(credentials);
    setSession({ token, operator });
  }, []);

  const signOut = useCallback(() => {
    setSession(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      token: session?.token ?? null,
      operator: session?.operator ?? null,
      status: session ? 'authenticated' : 'unauthenticated',
      isAuthenticated: Boolean(session),
      signIn,
      signOut,
    }),
    [session, signIn, signOut],
  );

  return (
    <AuthContext.Provider value={value}>
      <GqlTokenProvider value={value.token}>
        {/* A GraphQL 401 (expired/revoked JWT) signs the operator out so RequireAuth redirects
            to /login, instead of leaving the app half-authenticated. */}
        <GqlUnauthorizedProvider value={signOut}>{children}</GqlUnauthorizedProvider>
      </GqlTokenProvider>
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
